import { i18n } from "~vue/i18n";
import { formattingLocale } from "~vue/i18n/formattingLocale";
import { DateTime, type DateTimeFormatOptions } from "luxon";

// The closed set of ways a date is shown. Call sites say what kind of date it is; the active
// locale decides word order, separators and hour cycle. Adding a format is a design decision,
// not a per-site tweak - that is what keeps "4 de enero de 2026" from drifting back to
// "enero 4, 2026" (BRA-1047).
export type DateFormat = "date" | "date-long" | "month-day" | "month-day-long" | "month" | "month-year" | "weekday" | "time" | "datetime" | "relative";

export type DateInput = string | Date | DateTime | null | undefined;

export type DatePresentation = { kind: "empty"; text: "" } | { kind: "invalid"; text: string } | { kind: "valid"; text: string; datetime: string; absoluteText?: string };

export interface DateFormatContext {
    locale: string;
    now: DateTime;
}

const PRESETS: Record<Exclude<DateFormat, "relative">, DateTimeFormatOptions> = {
    date: DateTime.DATE_MED,
    "date-long": DateTime.DATE_FULL,
    "month-day": { month: "short", day: "numeric" },
    "month-day-long": { month: "long", day: "numeric" },
    month: { month: "short" },
    "month-year": { month: "short", year: "numeric" },
    weekday: { weekday: "long" },
    time: DateTime.TIME_SIMPLE,
    datetime: DateTime.DATETIME_MED,
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// A date-only string ("2026-01-04", e.g. a goal target date) is a calendar day, not an instant:
// it is parsed as local midnight and never zone-converted, so it cannot become the 3rd in
// New York. Everything else is an instant shown in the browser's zone; a luxon DateTime keeps
// whatever zone its caller already put it in (calendar events arrive in the user's zone).
function parse(value: string | Date | DateTime): { dt: DateTime; dateOnly: boolean } {
    if (DateTime.isDateTime(value)) {
        return { dt: value, dateOnly: false };
    }
    if (value instanceof Date) {
        return { dt: DateTime.fromJSDate(value), dateOnly: false };
    }
    return { dt: DateTime.fromISO(value), dateOnly: DATE_ONLY.test(value) };
}

export function presentDate(value: DateInput, format: DateFormat, context: DateFormatContext): DatePresentation {
    if (value === null || value === undefined || value === "") {
        return { kind: "empty", text: "" };
    }
    const { dt: parsed, dateOnly } = parse(value);
    if (!parsed.isValid) {
        return { kind: "invalid", text: typeof value === "string" ? value : "" };
    }
    const dt = parsed.setLocale(context.locale);
    const datetime = (dateOnly ? dt.toISODate() : dt.toISO()) ?? "";

    if (format !== "relative") {
        return { kind: "valid", text: dt.toLocaleString(PRESETS[format]), datetime };
    }
    if (dateOnly) {
        if (import.meta.env.DEV) {
            console.warn(`[formatDate] "relative" is not defined for the date-only value "${value}" - showing it as "date" instead`);
        }
        return { kind: "valid", text: dt.toLocaleString(PRESETS.date), datetime };
    }
    return {
        kind: "valid",
        text: dt.toRelative({ base: context.now }) ?? "",
        datetime,
        // "3 days ago" is ambiguous on its own; BaseDate puts this in hidden text for assistive tech.
        absoluteText: dt.toLocaleString(DateTime.DATETIME_MED),
    };
}

// Reading the vue-i18n locale ref here is what makes a computed() over formatDate re-render on
// a language switch; luxon's own Settings.defaultLocale (mirrored in i18n/index.ts) is not reactive.
export function currentDateFormatContext(): DateFormatContext {
    return { locale: formattingLocale(i18n.global.locale.value), now: DateTime.now() };
}

export function formatDate(value: DateInput, format: DateFormat): string {
    return presentDate(value, format, currentDateFormatContext()).text;
}

// For a label that should disappear when the value is malformed, where formatDate would show it raw.
export function formatValidDate(value: DateInput, format: DateFormat): string {
    const presentation = presentDate(value, format, currentDateFormatContext());
    return presentation.kind === "valid" ? presentation.text : "";
}

// For the few Intl calls that are not dates (compact numbers, zone names): the same locale the
// dates use, never a hardcoded "en".
export const activeLocale = (): string => formattingLocale(i18n.global.locale.value);

export type DateRangeFormat = "date" | "month-day" | "month-year";

export type DateRangeOptions = {
    // "never" is for labels too narrow for a year, where the year is clear from context (a week
    // tab next to its neighbours). Intl adds both years whenever a range crosses New Year, which
    // turns "Dec 29 – Jan 4" into "Dec 29, 2025 – Jan 4, 2026".
    year?: "auto" | "never";
};

// A range is one localized string, not two dates glued with a dash: Intl collapses the repeated
// fields the way each language expects ("Jan 4 – 10, 2026" / "4–10 ene 2026"). Luxon has no range
// formatter. Both ends are calendar days in the browser zone (date-only strings, or local DateTimes).
export function formatDateRange(
    start: DateInput,
    end: DateInput,
    format: DateRangeFormat,
    options: DateRangeOptions = {},
    context: DateFormatContext = currentDateFormatContext(),
): string {
    if (!start || !end) {
        return "";
    }
    const from = parse(start).dt;
    const to = parse(end).dt;
    if (!from.isValid || !to.isValid) {
        return "";
    }
    // Intl only knows instants and would re-read them in the browser zone, moving a UTC ISO week or
    // a far-ahead zone's day. Pinning each end's own wall-clock time to UTC keeps its calendar day.
    const formatter = new Intl.DateTimeFormat(context.locale, { ...PRESETS[format], timeZone: "UTC" });
    const fromDay = from.setZone("utc", { keepLocalTime: true }).toJSDate();
    const toDay = to.setZone("utc", { keepLocalTime: true }).toJSDate();
    const text = options.year === "never" ? withoutYear(formatter.formatRangeToParts(fromDay, toDay)) : formatter.formatRange(fromDay, toDay);
    // Newer ICU puts thin / narrow no-break spaces around the dash ("Aug 10\u2009–\u200916"). Plain
    // spaces keep the English output byte-identical to the hand-built ranges this replaced, and
    // keep text matching in specs and Playwright sane.
    return text.replaceAll(/[\u2009\u202f]/g, " ");
}

type RangePart = ReturnType<Intl.DateTimeFormat["formatRangeToParts"]>[number];

// Drops each year along with the separator that belongs to it: the one before it in "Dec 29, 2025",
// the one after it in "2025/12/29" or "2025년 12월". The range dash is "shared" and always stays.
function withoutYear(parts: RangePart[]): string {
    const kept: RangePart[] = [];
    for (let index = 0; index < parts.length; index++) {
        if (parts[index].type !== "year") {
            kept.push(parts[index]);
            continue;
        }
        const previous = kept.at(-1);
        if (previous?.type === "literal" && previous.source !== "shared") {
            kept.pop();
        } else if (parts[index + 1]?.type === "literal" && parts[index + 1].source !== "shared") {
            index++;
        }
    }
    return kept.map((part) => part.value).join("");
}
